import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { useRouter } from '@tanstack/react-router';
import { useGetDashboardSuspense } from '@uturn/api/v1';
import { Permission } from '@uturn/portal/abac';
import { LoadingSpan, Page } from '@uturn/portal/components';
import { getApiErrorMessage } from '@uturn/portal/utils';
import {
	Button,
	Callout,
	CalloutIcon,
	CalloutTitle,
	Icon,
	sonner,
} from '@uturn/ui-kit';
import { Suspense } from 'react';
import { AllowedTo } from 'react-abac';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const AccessDenied = () => {
	const { t } = useTranslation();
	const router = useRouter();
	return (
		<Callout variant="destructive">
			<CalloutIcon>
				<Icon icon={faInfoCircle} />
			</CalloutIcon>
			<CalloutTitle>
				<b>{t('pages.dashboard.forbidden.title', 'Access denied')}</b>:{' '}
				{t(
					'pages.dashboard.forbidden.description',
					'You need "Shipper Admin" role to access this page.',
				)}
			</CalloutTitle>
			<Button
				variant="destructive"
				size="sm"
				className="ml-auto flex items-center gap-3"
				onClick={(e) => {
					router.navigate({ to: '/' });
				}}
			>
				{t('pages.dashboard.forbidden.action', 'Back')}
			</Button>
		</Callout>
	);
};

const DashboardIframe = () => {
	const { t } = useTranslation();

	const {
		data: metabase,
		isError,
		error,
	} = useGetDashboardSuspense({
		query: {
			select: (data) => data.data,
		},
	});

	if (isError) {
		const message = getApiErrorMessage(error);
		sonner.error(
			t(
				'pages.dashboard.api-error',
				'Could not load dashboard. {{errorMessage}}',
				{ errorMessage: message },
			),
		);
		return <span>Loading failed.</span>;
	}

	return (
		<iframe
			className="h-screen w-full"
			title="uturn-dashboard"
			src={metabase.url}
		></iframe>
	);
};

export function DashboardInsights() {
	const { t } = useTranslation();

	return (
		<>
			<Helmet title={t('pages.dashboard.title', 'Data insight').toString()} />
			<Page title={t('pages.dashboard.title', 'Data insight').toString()}>
				<AllowedTo perform={Permission.METABASE_API} no={AccessDenied}>
					<Suspense fallback={<LoadingSpan />}>
						<DashboardIframe />
					</Suspense>
				</AllowedTo>
			</Page>
		</>
	);
}
